/**
 * The pick-one list's stylesheet, scoped under `wa-cmb-`. Every widget that uses `Combo`
 * prints this with its own sheet, BEFORE its own rules, so a widget's rule for its button
 * (`.wa-xyz .wa-cmb-btn{...}`) wins over the plain one here.
 *
 * Colours are the theme's tokens with a neutral fallback, the corner is `--wa-cmb-r` (the
 * theme's base radius when a widget sets none). Two widgets on one page print the same rules
 * twice, which costs nothing.
 *
 * The open list's arrival sits in a `prefers-reduced-motion:no-preference` block of its own.
 *
 * PURE.
 */
const P = "var(--color-primary,#6366f1)";
const FG = "var(--color-foreground,#111827)";
const BG = "var(--color-background,#fff)";
const MUTED = "var(--color-muted,#f3f4f6)";
const MFG = "var(--color-muted-foreground,#6b7280)";
const LINE = "var(--color-border,#e5e7eb)";
const R = "var(--wa-cmb-r,var(--radius-base,8px))";

export const COMBO_CSS = [
  ".wa-cmb{position:relative;display:flex;align-items:stretch;min-width:0}",
  `.wa-cmb-btn{box-sizing:border-box;font:inherit;font-size:15px;line-height:1.3;min-height:42px;width:100%;margin:0;display:flex;align-items:center;gap:8px;padding:0 12px;border:1px solid ${LINE};border-radius:${R};background:${BG};color:${FG};text-align:start;cursor:pointer}`,
  ".wa-cmb-val{flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}",
  `.wa-cmb-btn[data-empty] .wa-cmb-val{color:${MFG}}`,
  `.wa-cmb-chev{width:16px;height:16px;flex:none;color:${MFG}}`,
  '.wa-cmb-btn[aria-expanded="true"] .wa-cmb-chev{transform:rotate(180deg)}',
  `.wa-cmb>.wa-cmb-btn:focus-visible{outline:2px solid ${P};outline-offset:0;border-color:${P}}`,
  `.wa-cmb-btn[aria-expanded="true"]{border-color:${P}}`,
  ".wa-cmb-btn:disabled{opacity:.55;cursor:default}",
  // The open list: under the button, at least as wide, turned to the button's right edge
  // near the window's. `data-fit` keeps it exactly as wide as the button.
  `.wa-cmb-panel{position:absolute;z-index:40;top:calc(100% + 6px);left:0;box-sizing:border-box;min-width:100%;width:max-content;max-width:min(320px,calc(100vw - 32px));display:grid;gap:6px;padding:6px;border:1px solid ${LINE};border-radius:calc(${R} + 4px);background:${BG};color:${FG};box-shadow:0 22px 44px -20px rgb(0 0 0 / .38);text-align:start}`,
  // The rule above sets a display, which would beat the browser's own `[hidden]` rule.
  ".wa-cmb-panel[hidden]{display:none}",
  '.wa-cmb-panel[data-align="end"]{left:auto;right:0}',
  ".wa-cmb[data-fit]>.wa-cmb-panel{right:0;width:auto;max-width:none}",
  ".wa-cmb-search{position:relative;display:flex;align-items:center}",
  `.wa-cmb-sicon{position:absolute;left:10px;width:15px;height:15px;color:${MFG};pointer-events:none}`,
  `.wa-cmb-search input{box-sizing:border-box;font:inherit;font-size:14px;height:36px;width:100%;margin:0;padding:0 10px 0 32px;border:1px solid ${LINE};border-radius:${R};background:${BG};color:${FG};-webkit-appearance:none;appearance:none}`,
  `.wa-cmb-search input::placeholder{color:${MFG};opacity:1}`,
  `.wa-cmb-search input:focus-visible{outline:2px solid ${P};outline-offset:0;border-color:${P}}`,
  ".wa-cmb-list{list-style:none;margin:0;padding:0;max-height:264px;overflow-y:auto;overscroll-behavior:contain;outline:none;scrollbar-width:thin}",
  `.wa-cmb-opt{display:flex;align-items:center;gap:8px;margin:0;padding:8px 10px;border-radius:calc(${R} - 2px);font-size:15px;font-weight:400;line-height:1.3;color:${FG};cursor:pointer}`,
  ".wa-cmb-opt>span{flex:1;min-width:0;overflow-wrap:anywhere}",
  ".wa-cmb-tick{width:16px;height:16px;flex:none}",
  // A row that is not the pick keeps its tick's room empty: the static export's script moves one tick.
  ".wa-cmb-opt:not([aria-selected=\"true\"]) .wa-cmb-tick{visibility:hidden}",
  `.wa-cmb-opt[data-active]{background:${MUTED}}`,
  `.wa-cmb-opt[aria-selected="true"]{color:${P};font-weight:600}`,
  // The list itself takes the focus without a ring; the keyboard's place is the active row's.
  `.wa-cmb-list:focus-visible .wa-cmb-opt[data-active]{box-shadow:inset 0 0 0 2px ${P}}`,
  `.wa-cmb-none{margin:0;padding:8px 10px;font-size:14px;color:${MFG}}`,
  // The input a form posts the pick through (ComboField): invisible along the button's
  // bottom edge, where the browser's "please fill in" bubble then points.
  ".wa-cmb-proxy{position:absolute;left:0;bottom:0;box-sizing:border-box;width:100%;height:1px;min-height:0;margin:0;padding:0;border:0;opacity:0;pointer-events:none}",
  "@media (prefers-reduced-motion:no-preference){",
  "@keyframes wa-cmb-pop{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:none}}",
  ".wa-cmb-panel{animation:wa-cmb-pop .18s cubic-bezier(.22,1,.36,1)}",
  ".wa-cmb-chev{transition:transform .25s cubic-bezier(.22,1,.36,1)}",
  ".wa-cmb-btn{transition:border-color .15s ease,background-color .15s ease}",
  "}",
].join("");
